"use client";

import { Button } from "@workspace/ui/components";
import { cn } from "@workspace/ui/lib/utils";
import { SubscriptionPlan } from "@workspace/types";
import {
  Building,
  CircleCheck,
  Flame,
  Globe,
  LockKeyhole,
  Star,
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { JSX } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../components/tooltip";

const PLANS = [
  {
    id: "free",
    title: "Free",
    description:
      "For small teams ready to experience AI-powered sales coaching.",
    pricing: {
      annual: "$0",
      monthly: "$0",
    },

    pricingDescription: "Free forever",
    features: {
      title: "What's included:",
      list: [
        "Up to 10 sales reps",
        "1 manager seat",
        "Individual performance report",
        "10 call analyses per rep",
        "1 AI role-play per user",
      ],
    },
  },
  {
    id: "team",
    title: "Team",
    description:
      "For sales managers focused on coaching teams to consistently hit targets.",
    pricing: {
      annual: "$45",
      monthly: "$63",
    },
    pricingDescription: "Cancel anytime",
    features: {
      title: "Everything in Free, plus:",
      list: [
        "Ongoing call analysis",
        "Unlimited AI role-plays",
        "Unlimited manager seats",
        "Unlimited individual performance reports",
        "Dedicated Customer Success Manager",
      ],
    },
  },
  {
    id: "enterprise",
    title: "Enterprise",
    description:
      "For organizations requiring custom, scalable solutions and dedicated support.",
    pricing: {
      annual: "",
      monthly: "",
    },
    pricingDescription: "Let's build your plan.",
    features: {
      title: "Everything in Team, plus",
      list: [
        "On-premise deployment options",
        "Fine-tuned AI models",
        "Seamless CRM integration",
        "LMS integration",
        "Enterprise-Grade Security (SAML/SSO)",
      ],
    },
  },
] as const;

type PlanID = "free" | "team" | "enterprise";

const ICONS: Record<PlanID, JSX.Element> = {
  free: (
    <span className="p-3 rounded-[12px] bg-[rgba(110,196,255,0.20)] size-12">
      <Star className="stroke-[#6ec4ff] size-6" />
    </span>
  ),
  team: (
    <span className="p-3 rounded-[12px] bg-[rgba(156,140,255,0.20)] size-12">
      <Building className="stroke-[#9C8CFF] size-6" />
    </span>
  ),
  enterprise: (
    <span className="p-3 rounded-[12px] bg-[rgba(255,87,76,0.20)] size-12">
      <Globe className="stroke-[#FF574C] size-6" />
    </span>
  ),
};

type PlansProps = {
  ready: boolean;
  isLoading: boolean;
  variant: "primary" | "secondary";
  currentPlan?: SubscriptionPlan;
  defaultBillingCycle?: "monthly" | "annual";
  provider?: "awsmp" | "stripe";
  onSubscribe: (plan: SubscriptionPlan) => void;
  onUnsubscribe?: () => void;
};

export const Plans: FC<PlansProps> = ({
  onSubscribe,
  onUnsubscribe = () => { },
  ready,
  isLoading,
  provider = "stripe",
  variant = "primary",
  currentPlan = "free",
  defaultBillingCycle = "annual",
}) => {
  const [selectedSeats, setSelectedSeats] = useState<Record<PlanID, number>>({
    free: 1,
    team: 5,
    enterprise: 1,
  });
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">(
    defaultBillingCycle,
  );

  const isSubscriptionDisabled = (planId: PlanID, isActive: boolean) => {
    if (variant === "primary") {
      return false
    }
    if (provider === "awsmp") {
      return !planId.includes("enterprise")
    }
    return isLoading || !ready || (planId === "free" && isActive)

  }

  const buttonText = (id: PlanID) => {
    const text: Record<PlanID, Record<typeof variant, string>> = {
      free: {
        primary: "Sign Up for Free",
        secondary: "Update",
      },
      team: {
        primary: "Start Your 30-Day Trial",
        secondary: "Update",
      },
      enterprise: {
        primary: "Book a Demo",
        secondary: "Book a Demo",
      },
    };

    if (provider === "awsmp" && id !== "enterprise") {
      return <><LockKeyhole />Managed Via AWS</>
    }

    if (variant === "primary") {
      return text[id].primary;
    }

    if (id === "free" && isPlanActive(id)) {
      return "Current";
    }

    return isPlanActive(id) ? "Cancel Plan" : text[id][variant];
  };

  const updateSelectedSeats = (id: PlanID, action: "minus" | "plus") => {
    setSelectedSeats((prev) => ({
      ...prev,
      [id]:
        action === "minus"
          ? Math.max(prev[id] - 1, id === "team" ? 5 : 1)
          : Math.min(prev[id] + 1, 10),
    }));
  };

  const isPlanActive = (id: PlanID) => {
    if (id === "team") {
      return currentPlan.includes(billingCycle);
    }
    return currentPlan === id;
  };

  const getPlanName = (plan: SubscriptionPlan): string => {
    const names: Record<SubscriptionPlan, string> = {
      enterprise: "Enterprise",
      free: "Free",
      team_annual: "Team",
      team_monthly: "Team",
    };
    return names[plan];
  };

  const getSubscriptionPlan = (id: PlanID): SubscriptionPlan => {
    if (id === "team") {
      return billingCycle === "monthly" ? "team_monthly" : "team_annual";
    }
    return id;
  };

  const handleSubscribe = (id: PlanID) => {
    if (isPlanActive(id)) {
      return onUnsubscribe();
    }
    onSubscribe(getSubscriptionPlan(id));
  };

  useEffect(() => {
    setBillingCycle(defaultBillingCycle);
  }, [defaultBillingCycle]);

  return (
    <div className="flex flex-col">
      <div
        className={cn(
          "flex ",
          variant === "primary"
            ? "justify-center mb-14"
            : "justify-between mb-8",
        )}
      >
        {variant === "primary" && (
          <div className="flex rounded-[16px] border border-d-border px-[5px] py-1 h-fit bg-d-secondary">
            <Button
              variant={billingCycle === "monthly" ? "d-primary" : "ghost"}
              data-state={billingCycle === "monthly" ? "active" : "inactive"}
              onClick={() => setBillingCycle("monthly")}
              className="font-sfpro-rounded text-xl font-semibold data-[state=inactive]:text-d-gray h-12 border-2 data-[state=inactive]:border-transparent data-[state=inactive]:hover:bg-transparent"
            >
              Monthly
            </Button>
            <Button
              variant={billingCycle === "annual" ? "d-primary" : "ghost"}
              data-state={billingCycle === "annual" ? "active" : "inactive"}
              onClick={() => setBillingCycle("annual")}
              className="font-sfpro-rounded text-xl font-semibold data-[state=inactive]:text-d-gray h-12 border-2 data-[state=inactive]:border-transparent data-[state=inactive]:hover:bg-transparent"
            >
              Annually{" "}
              <span className="px-2 py-1 bg-white rounded-[8px] shadow-[0_4px_4px_0_rgba(41,37,36,0.12)] font-semibold text-[13px] ml-2 border-2 border-[rgba(255,255,255,.08)]">
                29% off
              </span>
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col px-5 flex-auto gap-3 lg:flex-row lg:gap-0 lg:px-0">
        {PLANS.map((plan) => {
          const isActive = isPlanActive(plan.id);
          return (
            <div
              key={plan.id}
              className={cn(
                "flex flex-auto flex-col w-full max-w-[376px] p-1.5 pt-2.5 gap-2.5",
                plan.id === "team" && {
                  "rounded-[28px] border border-[#eaeaea] bg-[#f9f9f9]":
                    variant === "secondary",
                  "rounded-[28px] border border-[#f3e4c9] bg-[#f3e4c9]":
                    variant === "primary",
                },
              )}
            >
              <div
                className={cn(
                  "flex h-5 text-d-gray text-sm font-medium",
                  plan.id === "team" ? "flex" : "hidden lg:flex",
                )}
              >
                {plan.id === "team" && (
                  <span className="flex gap-2 items-center justify-center w-full">
                    <Flame
                      className={cn(
                        "size-4",
                        variant === "primary" && "fill-d-gray",
                      )}
                    />
                    Most Popular
                  </span>
                )}
              </div>
              <div
                className={cn(
                  "flex flex-col rounded-[24px] border px-5 py-6 w-full h-full bg-white",
                  {
                    "border-[#f4e7b1]": variant === "primary",
                    "border-[#eaeaea]": variant === "secondary",
                  },
                )}
              >
                <div className="flex flex-col gap-5">
                  {ICONS[plan.id as PlanID]}
                  <div className="flex flex-col gap-2">
                    <h3 className="text-2xl font-semibold">{plan.title}</h3>
                    <p className="text-d-gray font-medium h-11 leading-[22px]">
                      {plan.description}
                    </p>
                  </div>

                  <div className="flex flex-col gap-3">
                    {plan.id !== "enterprise" ? (
                      <motion.div
                        animate
                        layout
                        transition={{ duration: 0.3, type: "spring" }}
                        className="flex items-end w-fit"
                      >
                        <AnimatePresence>
                          {plan.id === "team" && billingCycle === "annual" && (
                            <motion.span
                              initial={{ opacity: 0, marginRight: 0 }}
                              animate={{ opacity: 1, marginRight: 8 }}
                              exit={{ opacity: 0, width: 0, marginRight: 0 }}
                              transition={{ duration: 0.2 }}
                              className="text-[40px] font-extrabold line-through leading-10 text-d-secondary-gray"
                            >
                              {plan.pricing.monthly}
                            </motion.span>
                          )}
                        </AnimatePresence>
                        <span className="text-[40px] font-extrabold leading-10 mr-2">
                          {plan.pricing[billingCycle]}
                        </span>
                        <span className="text-d-gray text-sm font-medium">
                          user/month
                        </span>
                      </motion.div>
                    ) : (
                      <span className="text-[40px] font-extrabold leading-10">
                        Custom
                      </span>
                    )}
                    <span className="h-8 text-d-secondary-gray text-[13px] font-medium">
                      {plan.pricingDescription}
                    </span>
                  </div>

                  {/* <div className="flex items-center justify-between"> */}
                  {/*   <span className="text-[#020617] text-sm font-medium tracking-[-0.28px]"> */}
                  {/*     Additional seats: */}
                  {/*   </span> */}
                  {/*   <div className="flex items-center gap-2"> */}
                  {/*     <Button */}
                  {/*       onClick={() => updateSelectedSeats(plan.id, "minus")} */}
                  {/*       variant="link" */}
                  {/*     > */}
                  {/*       <Minus /> */}
                  {/*     </Button> */}
                  {/*     <span className="text-[#020617] text-sm font-medium size-5 flex justify-center"> */}
                  {/*       <AnimateNumber>{selectedSeats[plan.id]}</AnimateNumber> */}
                  {/*     </span> */}
                  {/*     <Button */}
                  {/*       onClick={() => updateSelectedSeats(plan.id, "plus")} */}
                  {/*       variant="secondary" */}
                  {/*     > */}
                  {/*       <Plus /> */}
                  {/*     </Button> */}
                  {/*   </div> */}
                  {/* </div> */}
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex h-fit my-6">
                      <Button
                        disabled={isSubscriptionDisabled(plan.id, isActive)}
                        variant={
                          variant === "secondary" && isActive
                            ? "secondary"
                            : "d-primary"
                        }
                        className={cn(
                          "h-12 w-full",
                          variant === "secondary" &&
                          "rounded-[6px] border-1 border-[#ffd93d] shadow-[0_2px_4px_0_rgba(30,30,30,0.06)] h-10 bg-[#FFD93D] bg-[image:unset] text-[#020617] text-sm font-medium",
                          variant === "secondary" && isActive && "bg-white",
                        )}
                        onClick={() => handleSubscribe(plan.id)}
                      >
                        {buttonText(plan.id)}
                      </Button>
                    </div>
                  </TooltipTrigger>
                  {provider === "awsmp" && plan.id !== "enterprise" && (<TooltipContent>This plan was purchased through AWS Marketplace and can’t be changed here.</TooltipContent>)}
                </Tooltip>

                <div className="flex flex-col gap-4">
                  <span className="text-d-gray text-[13px] font-medium">
                    {plan.features.title}
                  </span>
                  <ul className="flex flex-col gap-3">
                    {plan.features.list.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CircleCheck className="size-4 min-w-4 [&>circle]:stroke-[#24a554] [&>circle]:fill-[#24a554] [&>path]:stroke-white mt-[2px]" />
                        <span className="text-d-gray text-sm font-medium">
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
